import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { ReportService } from '../../services/reportService';
import { CallService } from '../../services/callService';
import { authService } from '../../services/authService';
import { notificationService } from '../../services/notificationService';
import {
  CategoryDeficiencyReportData,
  CategoryDeficiencyItem,
  CategoryDataQualityIssue,
} from '../../types/CategoryDeficiencyReport';
import { DetailedScoresCleanupResult } from '../../types/DetailedScoresCleanup';
import { ReportExportButton } from './ReportExportButton';

export const CategoryDeficiencyReport: React.FC = () => {
  const [report, setReport] = useState<CategoryDeficiencyReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [isTenantAdmin, setIsTenantAdmin] = useState(false);

  // Cleanup flow: dry-run preview drives the confirm dialog before any write.
  const [cleanupPreview, setCleanupPreview] = useState<DetailedScoresCleanupResult | null>(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupApplying, setCleanupApplying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // After a cleanup apply + refetch, the panel (and the button that had focus) unmount; these
  // move focus back to the report container so it never falls to <body> (WCAG 2.4.3).
  const reportContainerRef = useRef<HTMLDivElement>(null);
  const pendingFocusRef = useRef(false);

  // Default to last 30 days
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  // The data-quality panel and cleanup action are TenantAdmin-only (rules.md authorization matrix).
  useEffect(() => {
    authService.getUserAsync().then((user) => {
      setIsTenantAdmin(user?.role === 'TenantAdmin');
    });
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ReportService.getCategoryDeficiencyReport(startDate, endDate);
      setReport(data);
    } catch {
      setError('Failed to load category deficiency report.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Restore focus once the post-cleanup refetch has finished rendering.
  useEffect(() => {
    if (!loading && report && pendingFocusRef.current) {
      pendingFocusRef.current = false;
      reportContainerRef.current?.focus();
    }
  }, [loading, report]);

  const toggleRow = (categoryName: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(categoryName)) {
        next.delete(categoryName);
      } else {
        next.add(categoryName);
      }
      return next;
    });
  };

  const getStatusChip = (status: string) => {
    const config: Record<string, { color: 'error' | 'warning' | 'info' | 'success'; label: string }> = {
      Critical: { color: 'error', label: 'Critical' },
      Below: { color: 'warning', label: 'Below Threshold' },
      Acceptable: { color: 'info', label: 'Acceptable' },
      Strong: { color: 'success', label: 'Strong' },
    };
    const c = config[status] ?? { color: 'info' as const, label: status };
    return <Chip size="small" color={c.color} label={c.label} />;
  };

  const getCategoryStatusChip = (pct: number) => {
    if (pct < 50) return <Chip size="small" color="error" label="Critical" />;
    if (pct < 65) return <Chip size="small" color="warning" label="Below Threshold" />;
    if (pct <= 80) return <Chip size="small" color="info" label="Acceptable" />;
    return <Chip size="small" color="success" label="Strong" />;
  };

  // Kicks off a dry run; on success opens the confirm dialog with the preview numbers.
  const handleStartCleanup = async () => {
    setCleanupLoading(true);
    try {
      const preview = await CallService.cleanupDetailedScores(true);
      setCleanupPreview(preview);
      setConfirmOpen(true);
    } catch {
      // Error toast already surfaced by CallService.
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleConfirmCleanup = async () => {
    setCleanupApplying(true);
    try {
      const applied = await CallService.cleanupDetailedScores(false);
      notificationService.success(
        'Cleanup complete',
        `${applied.callsModified} call${applied.callsModified === 1 ? '' : 's'} updated. Category data is now clean.`
      );
      setConfirmOpen(false);
      setCleanupPreview(null);
      pendingFocusRef.current = true;
      await fetchReport();
    } catch {
      // Error toast already surfaced by CallService; keep the dialog open to allow retry.
    } finally {
      setCleanupApplying(false);
    }
  };

  const handleCancelCleanup = () => {
    if (cleanupApplying) return;
    setConfirmOpen(false);
    setCleanupPreview(null);
  };

  // Rendered above every branch — loading, error and empty included. A period that fails to load
  // or returns nothing is exactly when a user needs to change the period, and returning a bare
  // Alert leaves them with no control but to leave the tab and come back.
  const dateFilter = (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          label="Start Date"
          type="date"
          size="small"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label="End Date"
          type="date"
          size="small"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <Button variant="contained" size="small" onClick={fetchReport}>
          Apply
        </Button>
        <ReportExportButton
          report="category-deficiency"
          label="Category Deficiency"
          startDate={startDate}
          endDate={endDate}
          busy={loading}
        />
        {report && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {report.totalCallsAnalyzed} calls analyzed
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
          Dates are calendar days in the agency&apos;s timezone.
        </Typography>
      </Box>
    </Paper>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {dateFilter}
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress aria-label="Loading category deficiency report" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {dateFilter}
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!report) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {dateFilter}
        <Alert severity="info">
          No completed calls with scoring data found for the selected period. An export of this
          period will contain the report headings and no data rows.
        </Alert>
      </Box>
    );
  }

  const hasCategories = report.categories.length > 0;
  const showQualityPanel = isTenantAdmin && report.dataQualityIssues.length > 0;
  const weakest = hasCategories ? report.categories[0] : null;

  return (
    <Box
      ref={reportContainerRef}
      tabIndex={-1}
      sx={{ display: 'flex', flexDirection: 'column', gap: 3, '&:focus': { outline: 'none' } }}
    >
      {dateFilter}

      {/* Data-quality panel — TenantAdmin only */}
      {showQualityPanel && (
        <DataQualityPanel
          issues={report.dataQualityIssues}
          onCleanUp={handleStartCleanup}
          cleanupLoading={cleanupLoading}
          cleanupApplying={cleanupApplying}
        />
      )}

      {hasCategories && weakest ? (
        <>
          {/* Insight Summary */}
          <Alert severity="warning" icon={false}>
            <Typography variant="subtitle2">
              Weakest category: <strong>{weakest.categoryName}</strong> at {weakest.scorePercentage}% —{' '}
              {weakest.belowThresholdPercentage}% of calls scored below 60%.
              Targeted training in this area could improve overall agency scores.
            </Typography>
          </Alert>

          {/* Agency-wide Category Table */}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={40} />
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Avg Score</TableCell>
                  <TableCell align="right">% of Weight</TableCell>
                  <TableCell align="right">Calls Below 60%</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.categories.map((cat) => (
                  <CategoryRow
                    key={cat.categoryName}
                    category={cat}
                    expanded={expandedRows.has(cat.categoryName)}
                    onToggle={() => toggleRow(cat.categoryName)}
                    getStatusChip={getStatusChip}
                    getCategoryStatusChip={getCategoryStatusChip}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <Alert severity="info">
          {report.totalCallsAnalyzed === 0
            ? 'No completed calls with scoring data found for the selected period.'
            : 'No parseable scoring categories for the selected period.'}
        </Alert>
      )}

      {/* Cleanup confirmation */}
      <Dialog
        open={confirmOpen}
        onClose={handleCancelCleanup}
        aria-labelledby="cleanup-dialog-title"
        aria-describedby="cleanup-dialog-description"
      >
        <DialogTitle id="cleanup-dialog-title">Clean up unparseable category data?</DialogTitle>
        <DialogContent>
          <DialogContentText id="cleanup-dialog-description" component="div">
            {cleanupPreview && cleanupPreview.callsAffected > 0 ? (
              <>
                This will remove <strong>{cleanupPreview.invalidKeysFound}</strong> unparseable
                category {cleanupPreview.invalidKeysFound === 1 ? 'entry' : 'entries'} from{' '}
                <strong>{cleanupPreview.callsAffected}</strong>{' '}
                {cleanupPreview.callsAffected === 1 ? 'call' : 'calls'} (across all dates) and
                recompute each affected call&rsquo;s overall score from the remaining valid
                categories. This cannot be undone.
              </>
            ) : (
              <>No unparseable category data was found. Nothing will be changed.</>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCleanup} disabled={cleanupApplying} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmCleanup}
            variant="contained"
            color="warning"
            disabled={cleanupApplying || !cleanupPreview || cleanupPreview.callsAffected === 0}
            startIcon={cleanupApplying ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {cleanupApplying ? 'Cleaning up…' : 'Remove & recompute'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

interface DataQualityPanelProps {
  issues: CategoryDataQualityIssue[];
  onCleanUp: () => void;
  cleanupLoading: boolean;
  cleanupApplying: boolean;
}

const shortenCallId = (id: string) => (id.length > 8 ? `${id.slice(0, 8)}…` : id);

const DataQualityPanel: React.FC<DataQualityPanelProps> = ({
  issues,
  onCleanUp,
  cleanupLoading,
  cleanupApplying,
}) => {
  const distinctCalls = new Set(issues.flatMap((i) => i.callIds)).size;
  const entryWord = issues.length === 1 ? 'entry' : 'entries';
  const callWord = distinctCalls === 1 ? 'call' : 'calls';

  return (
    <Alert severity="warning" role="region" aria-label="Unparseable category data">
      <AlertTitle>Unparseable category data detected</AlertTitle>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {issues.length} unparseable category {entryWord} found across {distinctCalls} {callWord} in
        this period, excluded from the report above and stemming from a since-fixed analysis bug.
        Cleaning up scans and fixes affected calls across all dates.
      </Typography>

      <Box component="ul" sx={{ pl: 3, my: 1 }}>
        {issues.map((issue, issueIdx) => (
          <Box component="li" key={issueIdx} sx={{ mb: 0.5 }}>
            <Typography
              component="code"
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                bgcolor: 'action.hover',
                px: 0.5,
                borderRadius: 0.5,
                wordBreak: 'break-all',
              }}
            >
              {issue.rawNamePreview || '(blank)'}
            </Typography>{' '}
            <Typography component="span" variant="body2" color="text.secondary">
              — {issue.callCount} {issue.callCount === 1 ? 'call' : 'calls'}:
            </Typography>{' '}
            {issue.callIds.map((id, idx) => (
              <React.Fragment key={id}>
                <Link
                  component={RouterLink}
                  to={`/call/${id}`}
                  title={id}
                  aria-label={`Call ${id}`}
                  underline="hover"
                >
                  {shortenCallId(id)}
                </Link>
                {idx < issue.callIds.length - 1 ? ', ' : ''}
              </React.Fragment>
            ))}
          </Box>
        ))}
      </Box>

      <Button
        variant="outlined"
        color="warning"
        size="small"
        onClick={onCleanUp}
        disabled={cleanupLoading || cleanupApplying}
        startIcon={cleanupLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
        sx={{ mt: 1 }}
      >
        {cleanupLoading ? 'Scanning…' : 'Clean up'}
      </Button>
    </Alert>
  );
};

interface CategoryRowProps {
  category: CategoryDeficiencyItem;
  expanded: boolean;
  onToggle: () => void;
  getStatusChip: (status: string) => React.ReactNode;
  getCategoryStatusChip: (pct: number) => React.ReactNode;
}

const CategoryRow: React.FC<CategoryRowProps> = ({
  category,
  expanded,
  onToggle,
  getStatusChip,
  getCategoryStatusChip,
}) => {
  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton size="small" onClick={onToggle} aria-label="expand row">
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{category.categoryName}</TableCell>
        <TableCell align="right">
          {category.avgScore.toFixed(2)} / {category.maxPossibleScore.toFixed(2)}
        </TableCell>
        <TableCell align="right">{category.scorePercentage.toFixed(1)}%</TableCell>
        <TableCell align="right">
          {category.callsBelowThreshold} ({category.belowThresholdPercentage.toFixed(1)}%)
        </TableCell>
        <TableCell align="center">{getCategoryStatusChip(category.scorePercentage)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Operator Breakdown — {category.categoryName}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Operator</TableCell>
                    <TableCell align="right">Avg Score</TableCell>
                    <TableCell align="right">Score %</TableCell>
                    <TableCell align="right">Calls</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {category.operators.map((op) => (
                    <TableRow key={op.operatorId}>
                      <TableCell>{op.operatorName}</TableCell>
                      <TableCell align="right">{op.avgScore.toFixed(2)}</TableCell>
                      <TableCell align="right">{op.scorePercentage.toFixed(1)}%</TableCell>
                      <TableCell align="right">{op.callCount}</TableCell>
                      <TableCell align="center">{getStatusChip(op.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
